import React from 'react';
import { Link } from 'react-router-dom';

interface ButtonLinkProps {
  url: string;
  target?: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * A smart link component that handles different types of navigation:
 * - Anchor links (#section-xxx) use native <a> for proper scroll behavior
 * - External links (target="_blank") use native <a> with security attributes
 * - Internal page links use React Router's <Link> for client-side navigation
 */
export const ButtonLink: React.FC<ButtonLinkProps> = ({
  url,
  target,
  className,
  style,
  children,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  const isExternalLink = target === '_blank';
  const isAnchorLink = url.startsWith('#');

  // Use native <a> for anchor links and external links
  if (isAnchorLink || isExternalLink) {
    return (
      <a
        href={url}
        target={isExternalLink ? '_blank' : undefined}
        rel={isExternalLink ? 'noopener noreferrer' : undefined}
        className={className}
        style={style}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {children}
      </a>
    );
  }

  // Use React Router Link for internal navigation
  return (
    <Link
      to={url}
      className={className}
      style={style}
      onClick={onClick as any}
      onMouseEnter={onMouseEnter as any}
      onMouseLeave={onMouseLeave as any}
    >
      {children}
    </Link>
  );
};
